import { gql, useLazyQuery } from '@apollo/client';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import {
  AppContext,
  Generator,
  getDateOperators,
  getDefaultValue,
  getTranslatedText,
  getUnifiedConditions,
  SkeletonTableLoading,
  TableContainer,
  updateConditions
} from '@nile/components';
import {
  AccountClient,
  GET_INTEGRATIONS_BY_IDS,
  GET_PERSONALIZATION_BY_NAME,
  RMMClient
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import {
  assetDetailsTab,
  defaultOptions,
  INTEGRATIONS,
  INTEGRATIONS_NON_SOLO,
  module,
  pageSize
} from '@nile/rmm/src/modules/asset-management/constants/asset-list';
import { GET_WORK_STATION_META_INFO_V4 } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import {
  checkDynamicQuery,
  fetchValues,
  generateOptions,
  getAsyncOptions,
  getDynamicQuery,
  getVariables
} from '@nile/rmm/src/modules/asset-management/services/asset-filter-helper';
import {
  columnsConfig,
  getAssetClassVariables,
  getAttributeName,
  getColumnAccessor,
  getColumnCellType,
  getColumnWidth,
  getFieldsToBeRequested
} from '@nile/rmm/src/modules/asset-management/services/asset-list-helper';
import { TRASH_ASSET } from '@nile/rmm/src/modules/common/constants';
import RMMContext from '@nile/rmm/src/modules/common/contexts/RMMContext';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import { config } from '@nile/rmm/src/modules/home/components/sub-pages/rmm-advanced-filter/config';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { cloneDeep, isEmpty } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

const bulkAction = [TRASH_ASSET];

function AssetList(props) {
  const {
    activeView,
    entitiesMeta,
    intl,
    handleFilterChange,
    listInfo,
    onlyDefaultColumns,
    defaultColumnsToExclude,
    defaultFilters,
    defaultCondition
  } = props;
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [allColumns, setAllColumns] = useState([]);
  const [searchAttributes, setSearchAttributes] = useState([]);
  const [sortAttributes, setSortAttributes] = useState([]);
  const [defaultAttributes, setDefaultAttributes] = useState([]);
  const [processedAllColumns, setProcessedAllColumns] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [tableBodyLoader, setTableBodyLoader] = useState(false);
  const [columnPersonalization, setColumnPersonalization] = useState(null);

  const [tableLoader, setTableLoader] = useState(false);

  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const [configuredIntegrations, setConfiguredIntegrations] = useState(null);

  const appContext = useContext(AppContext);
  const tabContext = useContext(TicketContext);
  const rmmContext = useContext(RMMContext);
  const { chargeBeePlanId } = appContext?.user;

  const conditionRef = useRef();
  conditionRef.current = listInfo?.condition;

  const [fetchPersonalization] = useLazyQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      userId: appContext.user.userId,
      category: 'AssetView',
      name: '4001'
    },
    onCompleted({ getPersonalizationByCategoryAndName }) {
      setColumnPersonalization(
        getPersonalizationByCategoryAndName?.value
          ? JSON.parse(getPersonalizationByCategoryAndName?.value)
          : []
      );
    }
  });

  const fetchWorkStationMetaInfo = () => {
    RMMClient.query({
      query: GET_WORK_STATION_META_INFO_V4,
      variables: {}
    }).then((res) => {
      const {
        selectAttributes,
        searchAttributes,
        sortAttributes,
        defaultSelectedAttributes
      } = res.data.getWorkStationMetaInfoV4;
      setDefaultAttributes(defaultSelectedAttributes);
      setSearchAttributes(searchAttributes);
      setSortAttributes(sortAttributes);
      setAllColumns(selectAttributes);
      fetchPersonalization();
    });
  };

  const performActions = (col) => {
    switch (col.name) {
      case 'name':
        return {
          onClick: navToDetailsPage
        };
      default:
        break;
    }
  };

  const constructTableData = (cols, data) => {
    const columns = [];

    cols.map((col, idx) => {
      columns.push({
        Header: col.label,
        minWidth: getColumnWidth(col.name, true),
        width: getColumnWidth(col.name),
        accessor: getColumnAccessor(col),
        type: getColumnCellType(col, activeView),
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: true,
        editable: false,
        disableFilters: true,
        field: col,
        actions: performActions(col),
        helpers: {
          getDefaultValue,
          checkDynamicQuery,
          updateConditions: updateAllConditions,
          getDateOperators,
          getVariables: getVariables.bind(this, col),
          getDynamicQuery,
          generateOptions,
          fetchValues,
          getAsyncOptions
        }
      });
    });

    setColumnGroup([{ Header: 'Name', columns }]);
    setAssetList(data?.assets);
    setTotalCount(data?.listInfo.totalCount || 0);
  };

  /**
   *
   * Final function to fetch list data
   * @param {object} data
   * @param {object} filters
   * @public
   */

  const fetchAssetList = async (data) => {
    setTableBodyLoader(true);
    const { page: defaultPage } = defaultOptions;

    const options = cloneDeep(data);

    const assetCol = entitiesMeta
      .find((meta) => meta.name === module)
      .attributes?.find((el) => el.name === 'assetId');

    const { configuredCols } = getProcessedConfig({
      entitiesMeta,
      allColumns: [...allColumns, 'assetId'],
      intl,
      module,
      columnPersonalization:
        (!isEmpty(columnPersonalization) && [
          ...(columnPersonalization || []),
          assetCol
        ]) ||
        [],
      searchAttributes,
      defaultColumns: [...defaultAttributes, 'assetId'],
      onlyDefaultColumns,
      defaultColumnsToExclude
    });

    // default filters will be used only when this list is used in client details -> Asset list
    if (defaultFilters) {
      if (options.condition.operands.length === 0) {
        options.condition.operands.push(defaultFilters);
      }

      if (options.condition.operands.length > 0) {
        let hasClientFilter = options.condition.operands[0].operands.find(
          (el) => {
            return el.attribute === defaultFilters.operands[0].attribute;
          }
        );

        if (!hasClientFilter) {
          options.condition.operands[0].operands.push(
            defaultFilters.operands[0]
          );
        }
      }
    }

    const generator = new Generator({
      operation: 'getWorkStationList',
      variables: getAssetClassVariables(options?.page || defaultPage),
      fields: getFieldsToBeRequested(configuredCols)
    });

    const queryString = generator.queryBuilder();
    const assetQuery = gql`
      ${queryString.query}
    `;

    const response = await RMMClient.query({
      query: assetQuery,
      variables: {
        listInfo: {
          pageSize,
          page: options?.page || defaultPage,
          sort: options?.sort,
          condition: !isEmpty(defaultCondition) ? defaultCondition : null
        }
      },
      fetchPolicy: 'network-only'
    });
    const list = response?.data?.getWorkStationList;

    constructTableData(configuredCols, list);
    setTableLoader(false);
    setTableBodyLoader(false);
  };

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original;
    tabContext.openTab({
      module: assetDetailsTab,
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };

  const [fetchIntegrationsByIds] = useLazyQuery(GET_INTEGRATIONS_BY_IDS, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (res?.getIntegrationsByIds) {
        const activatedIntegrations = res?.getIntegrationsByIds.filter(
          (integration) => {
            return integration.activated === true;
          }
        );
        setConfiguredIntegrations(activatedIntegrations);
      }
    }
  });

  function tableChange(context, data) {
    if (data) {
      if (handleFilterChange) {
        handleFilterChange({
          ...listInfo,
          page: data?.page || defaultOptions.page,
          sort:
            !isEmpty(data) && 'sort' in data ? data.sort : defaultOptions.sort
        });
      } else {
        setCurrentPage(data?.page);
        fetchAssetList({
          page: data?.page || defaultOptions.page,
          sort:
            !isEmpty(data) && 'sort' in data ? data.sort : defaultOptions.sort
        });
      }
    }
  }

  function constructUnifiedConditions(updatedCondition) {
    let module = 'asset-view';
    const params = {
      conditions: listInfo?.condition,
      defaultCondition: config[module].defaultCondition,
      updatedCondition: updatedCondition
    };
    const complexCondition = getUnifiedConditions(params);
    handleFilterChange({
      ...listInfo,
      sort:
        !isEmpty(listInfo) && 'sort' in listInfo
          ? listInfo.sort
          : defaultOptions.sort,
      condition: complexCondition
    });
  }

  function updateAllConditions(condition, selectedField) {
    updateConditions({
      condition,
      selectedField,
      viewConditions: conditionRef?.current?.operands?.[0] || null,
      setConditions: constructUnifiedConditions
    });
  }

  useEffect(() => {
    if (!isEmpty(processedAllColumns) && configuredIntegrations !== null) {
      fetchAssetList({ ...listInfo });
    }
  }, [processedAllColumns, defaultCondition, configuredIntegrations]);

  useEffect(() => {
    if (!isEmpty(allColumns) && !isEmpty(entitiesMeta)) {
      const { configuredCols, allCols } = getProcessedConfig({
        entitiesMeta,
        allColumns,
        intl,
        module,
        columnPersonalization,
        searchAttributes,
        onlyDefaultColumns,
        defaultColumnsToExclude
      });
      setProcessedAllColumns(configuredCols);
      if (rmmContext !== 'rmm') {
        rmmContext.setColumnConfigurations({
          allColumns: [
            {
              name: 'columns',
              header: 'Columns',
              items: allCols
            }
          ],
          configuredColumns: configuredCols
        });
      }
    }
  }, [allColumns, entitiesMeta, columnPersonalization]);

  useEffect(() => {
    if (isEmpty(allColumns)) {
      fetchWorkStationMetaInfo();
    }
  }, []);

  useEffect(() => {
    setAllColumns([]);
    setProcessedAllColumns([]);
    setTableLoader(true);
    setColumnPersonalization(null);

    let integrationIds =
      chargeBeePlanId === 'solo' ? INTEGRATIONS : INTEGRATIONS_NON_SOLO;

    fetchIntegrationsByIds({
      variables: {
        integrationIds
      }
    });
  }, [props.id]);

  if (tableLoader || isEmpty(processedAllColumns)) {
    return <SkeletonTableLoading borderTop />;
  }

  const tableConditionOperands = listInfo?.condition?.operands?.[0]
    ?.joinOperator
    ? listInfo.condition.operands[0]?.operands
    : listInfo?.condition?.operands?.filter((el) => el.attribute) || [];
  const tableConditionsForFilter = tableConditionOperands.length
    ? {
        joinOperator: 'AND',
        operands: tableConditionOperands
      }
    : null;

  const context = {
    setRecords: (data) => {
      setRecords(data);
      if (rmmContext !== 'rmm') {
        rmmContext.setBulkRecords(data);
      }
    },
    records,
    viewConditions: tableConditionsForFilter
  };
  return (
    <TableContainer>
      <TableView
        isLoading={tableBodyLoader}
        context={context}
        noBulkSelect={
          !bulkAction.includes(activeView?.systemViewProperty || '')
        }
        columns={columnGroup}
        listInfo={isEmpty(listInfo) ? defaultOptions : listInfo}
        defaultColumnCells={columnsConfig}
        data={assetList}
        rowCount={pageSize}
        refreshTable={tableChange}
        getAttributeName={getAttributeName}
        skipPageReset={false}
        pagination={Math.ceil(totalCount / pageSize) > 1}
        pages={Math.ceil(totalCount / pageSize) || 0}
        activePage={currentPage || defaultOptions.page}
        recordCount={totalCount}
        fullHeight
        emptyState={{
          nodataImage: `${envConfig.imageRoot}svg/asset-emplty-stat.svg`,
          heading: getTranslatedText(
            intl,
            activeView?.systemViewProperty === 'TRASH_ASSET'
              ? 'asset.trash.noAssetsFound.heading'
              : 'asset.noAssetsFound.heading'
          ),
          description: getTranslatedText(
            intl,
            activeView?.systemViewProperty === 'TRASH_ASSET'
              ? 'asset.trash.noAssetsFound.desc'
              : 'asset.noAssetsFound.desc'
          )
        }}
      />
    </TableContainer>
  );
}

export default withRouter(injectIntl(withLDConsumer()(AssetList)));
